import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionSupplementsViewFragmentAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { AdministrationRouteType, SupplementsTypes } from './supplements-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { AdministrationType, DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.NUTRITION_SUPPLEMENTS_VIEW_FRAGMENT })
class SupplementsViewController extends BaseController {

  @Inject(NutritionSupplementsViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_DILUENT)
  fieldDiluentAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_DILUENT_DOSE)
  fieldDiluentDoseAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_DILUENT_UNIT_MEASURE)
  fieldDiluentUnitMeasureAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_DILUENT_VOLUME)
  fieldDiluentVolumeAPI!: FieldAPI;

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_STAGE_INFUSION_DURATION)
  fieldStageInfusionDurationAPI!: FieldAPI

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_INFUSION_RATE)
  fieldInfusionRateAPI!: FieldAPI

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_INFUSION_RATE_UNIT_MEASURE)
  fieldInfusionRateUnitMeasureAPI!: FieldAPI

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_INFUSION_DEVICE)
  fieldInfusionDeviceAPI!: FieldAPI

  @Inject(NutritionSupplementsViewFragmentAliases.FIELD_PREPARATION_GUIDANCE)
  fieldPreparationGuidanceAPI!: FieldAPI

  @OnMount(NutritionSupplementsViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionId, patientId } = this.historyService.getCurrentRouteParams()
    this.fetchSupplementsDetails(String(nutritionId), String(patientId))
  }

  @OnMount(NutritionSupplementsViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const supplementsView = this.context.get('supplementsView') as SupplementsTypes
    if (supplementsView) {
      const {
        material,
        administrationRoute,
        dose,
        measurementUnit,
        diluent,
        diluentDose,
        diluentUnitMeasure,
        totalVolume,
        interval,
        applicationTime,
        infusionSpeed,
        dosageType,
        infusionBomb,
        orientation,
        administrationRouteType
      } = supplementsView;

      this.formMainInformationsAPI.setValue({
        administrationRoute,
        applicationTime,
        diluent,
        diluentDose,
        diluentUnitMeasure,
        dosageType,
        dose,
        infusionBomb,
        infusionSpeed,
        interval,
        material,
        measurementUnit,
        orientation,
        totalVolume
      })

      this.showFieldsIfDiluentNotEmpty(diluent);
      this.showFieldsByAdministrationRouteType(administrationRouteType);
    }
  }

  @OnMount(NutritionSupplementsViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const supplementsView = this.context.get('supplementsView') as SupplementsTypes

    if (supplementsView) {
      const { duration, administration, hours } = supplementsView
      const startDate = moment(String(supplementsView.startDate)).utc().format(ISO_DATE_INPUT)
      const endDate = moment(String(supplementsView.endDate)).utc().format(ISO_DATE_INPUT)

      this.formPeriodValidityAPI.setValue({
        administration,
        duration,
        endDate,
        hours,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
      this.inputStartDateAPI.setValue(endDate)

      this.showFieldsByDurationType(supplementsView.durationType);
      this.showFieldsByAdministrationType(supplementsView.administrationType);
    }
  }

  @OnMount(NutritionSupplementsViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const supplementsView = this.context.get('supplementsView') as SupplementsTypes
    if (supplementsView) {
      const { prescriberDt, prescriberName, justification, observation } = supplementsView

      this.formAddicionalInformationsAPI.setValue({
        justification,
        observation,
        prescriberDt,
        prescriberName,
      })

      this.fieldJustificationAPI.setVisible(!!justification)
      this.fieldObservationAPI.setVisible(!!observation)
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName)
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt)
    }
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME)
  }

  private showFieldsByAdministrationRouteType(administrationRouteType: string): void {
    if (administrationRouteType !== AdministrationRouteType.ENTERAL
      && administrationRouteType !== AdministrationRouteType.URETHRAL_ROUTE) {
      this.fieldStageInfusionDurationAPI.setVisible(false);
      this.fieldInfusionRateAPI.setVisible(false);
      this.fieldInfusionRateUnitMeasureAPI.setVisible(false);
      this.fieldInfusionDeviceAPI.setVisible(false);
      this.fieldPreparationGuidanceAPI.setVisible(false);
    }
  }

  private showFieldsIfDiluentNotEmpty(diluent: string): void {
    if (diluent == null) {
      this.fieldDiluentAPI.setVisible(false)
      this.fieldDiluentDoseAPI.setVisible(false)
      this.fieldDiluentUnitMeasureAPI.setVisible(false)
      this.fieldDiluentVolumeAPI.setVisible(false)
    }
  }

  private fetchSupplementsDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_SUPPLEMENTS_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const supplementsView = response.body as SupplementsTypes;
        this.context.set({ supplementsView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-supplements',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default SupplementsViewController;